<template>
  <section class="customer-activity" aria-label="Customer activity" :aria-busy="loading">
    <header class="activity-heading">
      <div>
        <h3>Activity</h3>
        <p>Who changed what, and when. Open an entry to compare the saved values.</p>
      </div>
      <b-button variant="outline-secondary" :disabled="loading" @click="load">Refresh</b-button>
    </header>
    <form class="activity-filters" @submit.prevent="applyFilters">
      <label>Show<select v-model="category" class="form-select" @change="applyFilters">
        <option value="all">All activity</option>
        <option value="prices">Price changes</option>
      </select></label>
      <label>From <input v-model="dateFrom" class="form-control" type="date" /></label>
      <label>To <input v-model="dateTo" class="form-control" type="date" :min="dateFrom" /></label>
      <b-button type="submit" variant="outline-secondary" :disabled="loading">Apply dates</b-button>
      <span class="activity-timezone">Times shown in {{ timezone }}</span>
    </form>
    <p class="activity-notice">
      Detailed history starts when activity logging is enabled. Passwords and credentials are hidden.
    </p>
    <p v-if="error" role="alert">
      {{ error }} <button type="button" class="btn btn-link" @click="load">Try again</button>
    </p>
    <p v-else-if="loading" role="status">Loading activity…</p>
    <p v-else-if="!items.length" class="activity-empty">
      No recorded {{ category === 'prices' ? 'price changes' : 'changes'
      }}{{ dateFrom || dateTo ? ' in this date range' : ' yet' }}.
    </p>
    <div v-else class="activity-list">
      <div class="activity-columns" aria-hidden="true">
        <span></span><span>Date &amp; time</span><span>Changed by</span><span>Change</span>
      </div>
      <details v-for="item in items" :key="item.id" class="activity-entry">
        <summary>
          <time :datetime="item.createdAt">{{ date(item.createdAt) }}</time>
          <span class="activity-actor"><strong>{{ item.actorName }}</strong><small v-if="item.actorEmail !== item.actorName">{{ item.actorEmail }}</small></span>
          <span><strong>{{ summary(item) }}</strong><small>{{ item.changes.reduce((n, change) => n + change.fields.length, 0) }} changed
            {{ item.changes.reduce((n, change) => n + change.fields.length, 0) === 1 ? 'field' : 'fields' }} · View
            details</small></span>
        </summary>
        <div class="activity-details">
          <section v-for="(change, index) in item.changes" :key="index" class="activity-change">
            <h4>
              {{ change.entity }} <span class="activity-action">{{ change.action }}</span>
            </h4>
            <dl class="activity-context">
              <div v-for="(value, key) in change.context" :key="key">
                <dt>{{ label(String(key)) }}</dt>
                <dd>{{ key === 'carrier_handle' ? CARRIERS_NAMES[String(value)] || value : value }}</dd>
              </div>
            </dl>
            <div class="activity-table-scroll">
              <table class="table">
                <thead>
                  <tr>
                    <th scope="col">Field</th>
                    <th scope="col">Before</th>
                    <th scope="col">After</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="field in change.fields" :key="field.field">
                    <th scope="row">{{ label(field.field) }}</th>
                    <td>
                      <pre>{{ format(field.before, field.field) }}</pre>
                    </td>
                    <td>
                      <pre>{{ format(field.after, field.field) }}</pre>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </details>
    </div>
    <footer v-if="count > 20" class="activity-pagination">
      <b-button variant="outline-secondary" :disabled="page === 1 || loading" @click="changePage(-1)">
        Previous
      </b-button>
      <span>Page {{ page }} of {{ Math.ceil(count / 20) }} · {{ count }} entries</span>
      <b-button variant="outline-secondary" :disabled="page * 20 >= count || loading" @click="changePage(1)">
        Next
      </b-button>
    </footer>
  </section>
</template>

<script lang="ts">
import type { CustomerActivityItem, CustomerActivityCategory } from '@/types/Models/CustomerActivity'
import { onBeforeUnmount, ref, watch, defineComponent, type Ref, type PropType } from 'vue'
import { CARRIERS_NAMES } from '@/const/Carriers'
import CustomerActivityProxy from '@/proxies/Customer/CustomerActivityProxy'

export default defineComponent({
  name: 'CustomerActivity',
  props: {
    companyId: { type: String, required: true },
    initialCategory: { type: String as PropType<CustomerActivityCategory>, default: 'all' },
  },
  setup(props) {
    const category: Ref<CustomerActivityCategory> = ref<CustomerActivityCategory>(props.initialCategory ?? 'all')
    const items: Ref<CustomerActivityItem[]> = ref<CustomerActivityItem[]>([])
    const count: Ref<number> = ref(0)
    const loading: Ref<boolean> = ref(false)
    const error: Ref<string> = ref('')
    const page: Ref<number> = ref(1)
    const dateFrom: Ref<string> = ref('')
    const dateTo: Ref<string> = ref('')
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    let requestId = 0
    const label = (value: string): string =>
      value === 'carrier_handle'
        ? 'Carrier'
        : value === 'min_packages_amount'
          ? 'Minimum quantity'
          : value.replace(/_/g, ' ').replace(/^./, (first) => first.toUpperCase())
    const date = (value: string): string =>
      new Intl.DateTimeFormat('sv-SE', { dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(value))
    const format = (value: unknown, field = ''): string =>
      /^(is_|has_|hide_|should_)/.test(field) && (value === 0 || value === 1)
        ? value
          ? 'Yes'
          : 'No'
        : value === null || value === undefined
          ? 'Not set'
          : typeof value === 'boolean'
            ? value
              ? 'Yes'
              : 'No'
            : typeof value === 'object'
              ? JSON.stringify(value, null, 2)
              : String(value)
    const summary = (item: CustomerActivityItem): string =>
      [
        ...new Set(
          item.changes.map((change) =>
            [
              change.entity,
              change.context.carrier_handle ? CARRIERS_NAMES[String(change.context.carrier_handle)] : '',
              ...change.fields.slice(0, 2).map((field) => label(field.field)),
            ]
              .filter(Boolean)
              .join(' · '),
          ),
        ),
      ]
        .slice(0, 2)
        .join('; ')
    function load(): void {
      const id = ++requestId
      loading.value = true
      error.value = ''
      new CustomerActivityProxy(props.companyId, {
        page: page.value,
        category: category.value,
        dateFrom: dateFrom.value,
        dateTo: dateTo.value,
        timezone,
      })
        .all()
        .then((response) => {
          if (id !== requestId) return
          items.value = response.data ?? []
          count.value = response.count ?? 0
        })
        .catch(() => {
          if (id === requestId) error.value = 'Activity could not be loaded. Please try again.'
        })
        .finally(() => {
          if (id === requestId) loading.value = false
        })
    }
    function applyFilters(): void {
      page.value = 1
      load()
    }
    function changePage(delta: number): void {
      page.value += delta
      load()
    }
    watch(
      () => props.companyId,
      () => {
        items.value = []
        page.value = 1
        load()
      },
      { immediate: true },
    )
    onBeforeUnmount(() => {
      requestId++
    })
    return {
      category,
      items,
      count,
      loading,
      error,
      page,
      dateFrom,
      dateTo,
      timezone,
      requestId,
      label,
      date,
      format,
      summary,
      load,
      applyFilters,
      changePage,
      CARRIERS_NAMES,
    }
  },
})
</script>
